// Home page · "Surface hidden risks across all borrower entities" illustration
'use client';

import { Banknote, Building2, CircleUserRound } from 'lucide-react';
import DemoWrapper from '@/components/DemoWrapper';
import { BLUE, ContactRing, Beam, GridBg, Link, MAP_CSS, MUTED, Pill, RecordNode, STATUS, StatusDot, TEXT, elbow, fadeIn, rimLine, useMapGo, type Pt, type Status } from './MapKit';

/*
 * One borrower, their three entities and each entity's loans, read left to right. Beams reach out from the borrower to
 * each entity, then from each entity to its loans, lighting every loan's status; the one in foreclosure turns its
 * entity red.
 */

// made-up entities and loans
type Loan = { amount: string; kind: string; s: Status };
const ENTITIES: { name: string; loans: Loan[] }[] = [
  { name: 'MVT Holdings Inc', loans: [{ amount: '$450k', kind: 'Bridge', s: 'ok' }, { amount: '$1.2M', kind: 'Refinance', s: 'ok' }] },
  { name: '451 Broad LLC', loans: [{ amount: '$850k', kind: 'Construction', s: 'ok' }, { amount: '$2.1M', kind: 'Acquisition', s: 'risk' }] },
  { name: 'Walnut Partners LP', loans: [{ amount: '$2.4M', kind: 'DSCR', s: 'ok' }, { amount: '$900k', kind: 'Refinance', s: 'ok' }] },
];
const RISKY = 1; // the entity with the foreclosure
const RED_TEXT = '#b91c1c';

// timings (seconds after the map comes into view)
const BEAM = 0.5;
const T_ENTITY = (i: number) => 0.45 + i * 0.15;
const T_LOAN = (i: number, k: number) => 1.25 + i * 0.25 + k * 0.1;
// the risky entity turns red when its own beam arrives, in order with the others
const T_FLAG = T_ENTITY(RISKY) + BEAM;

// a sideways tree link: out of A's right side to a rail at x, along the rail, then into B's left side
const across = (x: number) => (A: Pt, B: Pt) => `M${A.x + A.r + 3} ${A.y}H${x}V${B.y}H${B.x - B.r - 3}`;
// the same link minus the stretch every link shares, so beams on that stretch don't stack up into a heavier line
const acrossFromRail = (x: number) => (A: Pt, B: Pt) => `M${x} ${A.y}V${B.y}H${B.x - B.r - 3}`;
const RAIL = 128;

type Layout = {
  borrower: Pt; entities: Pt[]; loans: Pt[][];
  trunk: (a: Pt, b: Pt) => string; branch: (a: Pt, b: Pt) => string;
  // desktop only: the shared first stretch of the trunk, drawn once, and each link's own remainder
  stem?: (a: Pt) => string; twig?: (a: Pt, b: Pt) => string;
};

// desktop and tablet: borrower on the left, entities in the middle, loans on the right, each labeled beside or under
// its node so no line runs through any text
const COLS = [70, 170, 270];
const LOAN_X = [[346, 331], [333, 348], [352, 336]];
const WIDE: Layout = {
  borrower: { x: 60, y: 170, r: 24 },
  entities: COLS.map(y => ({ x: 196, y, r: 18 })),
  // loans sit at uneven distances so the column reads loose rather than ruled
  loans: COLS.map((y, i) => [y - 24, y + 24].map((ly, k) => ({ x: LOAN_X[i][k], y: ly, r: 11 }))),
  trunk: across(RAIL),
  stem: b => `M${b.x + b.r + 3} ${b.y}H${RAIL}`,
  twig: acrossFromRail(RAIL),
  // loans branch off each entity on straight diagonals
  branch: rimLine,
};
// phones: the borrower at the top left, entities down the side, each loan on its own line under its entity
const ROWS = [100, 205, 310];
const NARROW: Layout = {
  borrower: { x: 30, y: 40, r: 22 },
  entities: ROWS.map(y => ({ x: 70, y, r: 16 })),
  loans: ROWS.map(y => [y + 34, y + 62].map(ly => ({ x: 104, y: ly, r: 10 }))),
  trunk: elbow,
  branch: elbow,
};

function Tree({ l, narrow }: { l: Layout; narrow?: boolean }) {
  const b = l.borrower;
  const nameColor = (i: number) => (i === RISKY ? RED_TEXT : TEXT);
  return (
    <>
      {/* quiet links first, so beams draw over them */}
      {l.entities.map((e, i) => <Link key={i} d={l.trunk(b, e)} />)}
      {l.entities.map((e, i) => l.loans[i].map((n, k) => <Link key={`${i}-${k}`} d={l.branch(e, n)} />))}

      {/* beams: borrower to entities, then entities to loans */}
      {l.stem && <Beam d={l.stem(b)} delay={T_ENTITY(0) - 0.2} dur={0.25} />}
      {l.entities.map((e, i) => <Beam key={i} d={(l.twig ?? l.trunk)(b, e)} delay={T_ENTITY(i)} dur={BEAM} />)}
      {l.entities.map((e, i) => l.loans[i].map((n, k) => (
        <Beam key={`${i}-${k}`} d={l.branch(e, n)} delay={T_LOAN(i, k)} dur={BEAM} color={ENTITIES[i].loans[k].s === 'risk' ? STATUS.risk : BLUE} />
      )))}

      {/* borrower */}
      <RecordNode n={b} icon={CircleUserRound} tint="#eff6ff" iconColor={BLUE} />
      <ContactRing n={b} delay={0.1} />
      {narrow ? (
        <>
          <text x={b.x + b.r + 12} y={b.y - 4} className="rm-name" fill={TEXT}>Michael Torres</text>
          <text x={b.x + b.r + 12} y={b.y + 13} className="rm-sub" fill={MUTED}>Real Estate Investor</text>
        </>
      ) : (
        <>
          <text x={b.x} y={b.y + b.r + 22} textAnchor="middle" className="rm-name" fill={TEXT}>Michael Torres</text>
          <text x={b.x} y={b.y + b.r + 38} textAnchor="middle" className="rm-sub" fill={MUTED}>Real Estate Investor</text>
        </>
      )}

      {/* entities */}
      {l.entities.map((e, i) => (
        <g key={i}>
          <RecordNode n={e} icon={Building2} />
          {i === RISKY
            ? <><ContactRing n={e} delay={T_FLAG} color={STATUS.risk} /><StatusDot n={e} status="risk" delay={T_FLAG} /></>
            : <ContactRing n={e} delay={T_ENTITY(i) + BEAM} />}
          {narrow
            ? <text x={e.x + e.r + 12} y={e.y + 4} className="rm-name" fill={nameColor(i)}>{ENTITIES[i].name}</text>
            : <text x={e.x} y={e.y + e.r + 18} textAnchor="middle" className="rm-name" fill={nameColor(i)}>{ENTITIES[i].name}</text>}
        </g>
      ))}

      {/* loans, each lighting its status when its beam arrives */}
      {l.loans.map((row, i) => row.map((n, k) => {
        const loan = ENTITIES[i].loans[k], t = T_LOAN(i, k) + BEAM;
        const risk = loan.s === 'risk';
        return (
          <g key={`${i}-${k}`}>
            <RecordNode n={n} icon={Banknote} />
            {loan.s !== 'ok' && <StatusDot n={n} status={loan.s} delay={t} />}
            <text x={n.x + n.r + 9} y={n.y + 4} className="rm-sub rm-a" style={fadeIn(t)}>
              <tspan fill={risk ? RED_TEXT : TEXT} fontWeight={600}>{loan.amount}</tspan>
              <tspan fill={risk ? RED_TEXT : MUTED}> {loan.kind}</tspan>
            </text>
          </g>
        );
      }))}

      {/* the foreclosure flag: beside the risky entity's name on phones, under it on wider screens */}
      {narrow
        ? <Pill x={244} y={l.entities[RISKY].y} w={76} h={18} text="Foreclosure" delay={T_FLAG} color={STATUS.risk} bg="#fef2f2" ink={RED_TEXT} />
        : <Pill x={l.entities[RISKY].x} y={l.entities[RISKY].y + 51} w={76} h={18} text="Foreclosure" delay={T_FLAG} color={STATUS.risk} bg="#fef2f2" ink={RED_TEXT} />}
    </>
  );
}

export default function HomeHiddenRisks() {
  const [goRef, goClass] = useMapGo<HTMLDivElement>();
  return (
    <DemoWrapper className="ex-illo bg-white rounded-md border border-slate-200 ui-shadow relative overflow-hidden h-[501px] sm:h-[342px] md:h-[400px] lg:h-[433px] xl:h-[400px]">
      <GridBg />
      <div ref={goRef} className={`${goClass} relative w-full h-full p-4`}>
        <style>{MAP_CSS}</style>
        <svg viewBox="0 0 480 340" className="hidden sm:block w-full h-full" fill="none" aria-hidden="true">
          <Tree l={WIDE} />
        </svg>
        <svg viewBox="0 0 320 390" className="sm:hidden w-full h-full" fill="none" aria-hidden="true">
          <Tree l={NARROW} narrow />
        </svg>
      </div>
    </DemoWrapper>
  );
}
